import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { MarkerUrls, SELECTED_Z_INDEX } from '../feature.model';
import { v4 as uuidv4 } from 'uuid';
import { StandardFeature } from '../standard/standard.feature';

enum MarkerColors {
  Gray = 'a09daf',
  Blue = '1971c2',
  Green = '3a8e06'
}

enum EngineStates {
  Off = 'Off',
  On = 'On',
  Idle = 'Idle'
}

export class AssetSamsaraVehicleFeature extends StandardFeature {
  protected markerIconOptions = {
    size: new google.maps.Size(50, 50),
    anchor: new google.maps.Point(25, 25)
  };

  buildMarkerImageUrls(layer: Layer, dataset: Dataset, document: Document, cacheBuster: number): MarkerUrls {
    const { heading_degrees, engine_state } = document;

    let rotation = '0';
    if (Boolean(heading_degrees)) {
      // for the given track heading direction, round to the nearest reasonable heading rotation; the server will render any heading by interpreting this number in degrees
      let degree = parseInt(heading_degrees);
      const roundingFactor = degree % 15;

      if (roundingFactor < 8) {
        // round down; 0 unchanged
        degree -= roundingFactor;
      } else {
        // round up
        degree += roundingFactor;
      }
      rotation = degree.toString();
    }

    let color = MarkerColors.Blue;

    if (engine_state === EngineStates.Off) {
      color = MarkerColors.Gray;
    } else if (engine_state === EngineStates.On) {
      color = MarkerColors.Green;
    }

    return {
      normal: `/api/v1/meta/markers/samsara-vehicle-sedan/${color}/${rotation}/0/normal/samsara-vehicle-sedan.png`,
      selected: `/api/v1/meta/markers/samsara-vehicle-sedan/${color}/${rotation}/0/selected/samsara-vehicle-sedan.png`
    };
  }

  addPoint(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];
    
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.markerUrls.selected,
        ...this.markerIconOptions
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      geoField
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', event => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          offsetY: 75,
          layer,
          dataset,
          document,
          offsetBelow: event.domEvent.target.clientHeight / 2
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.googleMapFeatures.markers.push(marker);
    this.clusterService.registerMarkerAdd(this.layerUId, marker);

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  setMarkersSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.markerUrls.selected,
        ...this.markerIconOptions
      });
    }
  }

  setMarkersNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);

      marker.setIcon({
        url: this.markerUrls.normal,
        ...this.markerIconOptions
      });
    }
  }
}
